import type { SiteId } from "~/hec/utils/site-id";

// Per-site PDP eligibility label. Drives the SKU-area summary pill on all
// three sites — SFRA's `showEligibility` pref defaults true with no per-site
// override, so WDH shows it on prod too (SFRA `app_wdh` common.properties +
// facetUtils.js `getEligibilityText` carry the combined "FSA/HSA Eligible"
// copy, not a single program). SFCC-11640: WDH was wrongly `null` on the
// assumption WDH products aren't FSA/HSA-eligible — they are (same
// `c_isEligible` master attr).
//
// NOTE: the below-ATC / sticky / optimistic "100% … ELIGIBLE" badge is a
// Workers-only pairing with the free-shipping badge, additionally gated on
// `SHOWS_FREE_SHIPPING_BADGE` (FSA/HSA only) — so a non-null WDH label here
// surfaces the pill but NOT that badge. Don't assume non-null ⇒ every surface.
export const ELIGIBILITY_LABEL_BY_SITE: Record<SiteId, string | null> = {
  HSASTORE: "HSA Eligible",
  FSASTORE: "FSA Eligible",
  WDH: "FSA/HSA Eligible",
};

// The bare site identifier behind the label above ("FSA" / "HSA" / "FSA/HSA"),
// used by the "100% … ELIGIBLE" badge and the telehealth value-prop copy
// (ET-216). Declared rather than regex-stripped off the display label: three
// call sites were doing `label?.replace(/ Eligible$/, "")` with divergent
// fallbacks (`?? ""` vs `|| undefined`), and recovering a data value from
// presentation copy breaks the moment the label wording changes.
export const ELIGIBILITY_SITE_LABEL_BY_SITE: Record<SiteId, string | null> = {
  HSASTORE: "HSA",
  FSASTORE: "FSA",
  WDH: "FSA/HSA",
};

// ET-216: anchor id on the below-the-fold Builder section wrapper
// (`pdp-inline-content`), so the telehealth column's "How it Works" link has a
// jump target. Lives here rather than in the telehealth folder so
// product-details.tsx can read it without pulling the value-prop icon
// components into the PDP entry chunk.
export const HOW_IT_WORKS_ANCHOR_ID = "pdp-how-it-works";

// Also gates the PDP "100% … ELIGIBLE" badge (below-ATC / sticky / optimistic):
// that badge is the eligibility+free-shipping pairing, so it renders only where
// free shipping does (FSA/HSA). WDH shows eligibility via the SKU-area pill
// instead (SFCC-11640) — see ELIGIBILITY_LABEL_BY_SITE note above.
export const SHOWS_FREE_SHIPPING_BADGE: Record<SiteId, boolean> = {
  HSASTORE: true,
  FSASTORE: true,
  WDH: false,
};

// Caring Mill compare-to link variant — FSA + HSA render the styled blue-box
// link (leaf mark + bold compare price); WDH renders the plain inline
// "Compare to X at $Y" message instead. (SFCC-11335: HSA matches FSA box.)
export const SHOWS_CARING_MILL_COMPARE_LINK: Record<SiteId, boolean> = {
  HSASTORE: true,
  FSASTORE: true,
  WDH: false,
};
